/**
 * Human-readable BUDOVA address (popup INFORMACE / pick list / display name).
 * Pattern: č.p. / č.ev. …, ulice, PSČ obec — fallback parc. … when address parts missing.
 * The prefix comes from `typCisla` (`E` → „č.ev.“, jinak „č.p.“) — never hardcode it.
 * Use {@link formatBudovaDisplayName} as the object title (never č.p. alone).
 * WF / Objekty k připojení: use `omitCp` + {@link formatBudovaCpLabel} for a separate „č.p.“ column.
 *
 * Do not pass Status ACQ (`statusAcq` / `souhlasSmlouva`) as an address.
 * Report street/city fields belong on {@link BudovaAddressLabelInput.acquisitionAddress}.
 */

import { formatHouseNumberLabel, normalizeTypCisla, type TypCisla } from './typ-cisla.util';

/** Address fields copied from the acquisition *report* — not Status ACQ. */
export interface BudovaAcquisitionAddress {
  cisloPopisne?: string | null;
  /** `P` = číslo popisné, `E` = číslo evidenční; chybí → `P`. */
  typCisla?: TypCisla | string | null;
  ulice?: string | null;
  mesto?: string | null;
  parcelNumber?: string | null;
}

export interface BudovaAddressLabelInput {
  addressPoint?: {
    cisloPopisne?: string | null;
    /** `P` = číslo popisné, `E` = číslo evidenční; chybí → `P`. */
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
    psc?: string | null;
  } | null;
  /** Address fallback from the acquisition report (č.p. / ulice / město / parcela). */
  acquisitionAddress?: BudovaAcquisitionAddress | null;
  parcelNumber?: string | null;
}

export interface FormatBudovaAddressLabelOptions {
  /** When true, omit č.p. from the label (for tables with a dedicated „č.p.“ column). */
  omitCp?: boolean;
}

function readAddressText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Pick only address fields from an acquisition report (or any mixed ACQ payload).
 * Status-only map ACQ (`statusAcq` / `souhlasSmlouva`) yields `null`.
 */
export function toBudovaAcquisitionAddress(source: unknown): BudovaAcquisitionAddress | null {
  if (!source || typeof source !== 'object') return null;
  const rec = source as Record<string, unknown>;
  const cisloPopisne = readAddressText(rec['cisloPopisne']);
  const typCisla = normalizeTypCisla(rec['typCisla']);
  const ulice = readAddressText(rec['ulice']);
  const mesto = readAddressText(rec['mesto']);
  const parcelNumber = readAddressText(rec['parcelNumber']);
  if (!cisloPopisne && !ulice && !mesto && !parcelNumber) return null;
  return { cisloPopisne, typCisla, ulice, mesto, parcelNumber };
}

/** Map a typical object payload onto {@link BudovaAddressLabelInput} without leaking Status ACQ. */
export function toBudovaAddressLabelInput(obj: {
  addressPoint?: BudovaAddressLabelInput['addressPoint'];
  acquisition?: unknown;
  acquisitionAddress?: BudovaAcquisitionAddress | null;
  parcelNumber?: string | null;
} | null | undefined): BudovaAddressLabelInput {
  return {
    addressPoint: obj?.addressPoint ?? null,
    acquisitionAddress: obj?.acquisitionAddress ?? toBudovaAcquisitionAddress(obj?.acquisition),
    parcelNumber: obj?.parcelNumber ?? null,
  };
}

/**
 * Typ čísla domu pro tento objekt — z address pointu, jinak z ACQ adresy, jinak `P`.
 * Adresní bod má přednost: je to zdroj pravdy z RÚIAN.
 */
export function resolveBudovaTypCisla(
  input: BudovaAddressLabelInput | null | undefined,
): TypCisla {
  const ap = input?.addressPoint;
  const acq = input?.acquisitionAddress;
  const fromAp = ap?.cisloPopisne?.trim() ? normalizeTypCisla(ap.typCisla) : null;
  return fromAp ?? normalizeTypCisla(acq?.typCisla) ?? 'P';
}

/**
 * Číslo pro samostatný sloupec s hlavičkou `TYP_CISLA_COLUMN_LABEL` („č.p. / č.ev.“).
 * Popisné zůstává holé (`23/5`), evidenční dostane prefix (`č.ev. 16`), aby byl
 * smíšený sloupec jednoznačný bez dalšího sloupce navíc.
 */
export function formatBudovaCpLabel(input: BudovaAddressLabelInput | null | undefined): string {
  if (!input) return '';
  const ap = input.addressPoint;
  const acq = input.acquisitionAddress;
  const cp = readAddressText(ap?.cisloPopisne) ?? readAddressText(acq?.cisloPopisne) ?? '';
  if (!cp) return '';
  const orient = readAddressText(ap?.orientacni) ?? '';
  const bare = orient ? `${cp}/${orient}` : cp;
  return resolveBudovaTypCisla(input) === 'E' ? formatHouseNumberLabel('E', cp, orient) : bare;
}

export function formatBudovaAddressLabel(
  input: BudovaAddressLabelInput | null | undefined,
  options?: FormatBudovaAddressLabelOptions,
): string | null {
  if (!input) return null;

  const ap = input.addressPoint;
  const acq = input.acquisitionAddress;
  const cp = readAddressText(ap?.cisloPopisne) ?? readAddressText(acq?.cisloPopisne) ?? '';
  const orient = readAddressText(ap?.orientacni) ?? '';
  const ulice = readAddressText(ap?.ulice) ?? readAddressText(acq?.ulice) ?? '';
  const obec = readAddressText(ap?.obec) ?? readAddressText(acq?.mesto) ?? '';
  const psc = readAddressText(ap?.psc) ?? '';
  const cpPart = options?.omitCp ? '' : formatHouseNumberLabel(resolveBudovaTypCisla(input), cp, orient);
  const misto = [ulice, [psc, obec].filter(Boolean).join(' ').trim()].filter(Boolean).join(', ');
  const address = [cpPart, misto].filter(Boolean).join(', ');
  if (address) return address;

  const parcel =
    readAddressText(input.parcelNumber) ?? readAddressText(acq?.parcelNumber) ?? '';
  if (parcel) return `parc. ${parcel}`;
  return null;
}

/** Display name / title for a BUDOVA — full address, not č.p. alone. */
export function formatBudovaDisplayName(
  input: BudovaAddressLabelInput | null | undefined,
  fallback = '',
): string {
  return formatBudovaAddressLabel(input) ?? fallback;
}

/**
 * Coalesced address fields for RD export / list columns.
 * Prefer `address_points`, then acquisition report, then `objects.parcel_number`
 * (and ACQ `raw_data` for orientační / parcela when columns are empty).
 * Empty strings never block a later non-empty source (`??` alone is not enough).
 */
export interface BudovaRdAddressFields {
  mesto: string;
  ulice: string;
  cisloPopisne: string;
  typCisla: TypCisla;
  orientacni: string | null;
  parcelNumber: string;
}

export function resolveBudovaRdAddressFields(
  input:
    | (BudovaAddressLabelInput & {
        acquisitionRawData?: Record<string, unknown> | null;
      })
    | null
    | undefined,
): BudovaRdAddressFields {
  const ap = input?.addressPoint;
  const acq = input?.acquisitionAddress;
  const raw = input?.acquisitionRawData ?? {};

  const cisloPopisne =
    readAddressText(ap?.cisloPopisne) ?? readAddressText(acq?.cisloPopisne) ?? '';
  const orientacni =
    readAddressText(ap?.orientacni) ??
    readAddressText(raw['cisloOrientacniA']) ??
    null;
  const ulice = readAddressText(ap?.ulice) ?? readAddressText(acq?.ulice) ?? '';
  const mesto = readAddressText(ap?.obec) ?? readAddressText(acq?.mesto) ?? '';
  const parcelNumber =
    readAddressText(input?.parcelNumber) ??
    readAddressText(acq?.parcelNumber) ??
    readAddressText(raw['parcelNumber']) ??
    '';

  return {
    mesto,
    ulice,
    cisloPopisne,
    typCisla: resolveBudovaTypCisla(input),
    orientacni,
    parcelNumber,
  };
}
